import React from 'react';
import {
  AUDIO_CARD_GAP,
  AUDIO_CARD_H,
  AUDIO_CARD_W,
  BRANCH_STEM,
  C,
  CIRCLE_R,
  PLUS_R,
  audioUrl,
  halfW,
  normalizeAudioField,
} from '../flowCanvas.constants';
import AudioWaveformCell from './AudioWaveformCell';
import audioLoopIcon from '../../v2/CustomisationWorkspace/WorkspaceAssets/ModuleIcons/audio_loop.png';

const PAD = 10;
const HEADER_H = 28;
const WRAPPER_HEADER_H = 36;
const WAVE_H = 26;
const CONTROL_CLUSTER_GAP = 8;

export default function AudioStackNode({
  step,
  stepX,
  stepCY,
  parentStep,
  parentStepHalfW,
  parentStepX,
  parentStepY,
  nextStep,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  sharedBranchX,
  items,
  activeStepId,
  activeGraphicIndex,
  slotVarName,
  onAudioClick,
  onAddItemClick,
  onInfoClick,
  onRemoveAudio,
  hideSourceTrunk,
  hideExitTrunk,
  sharedEntryBottomY,
  sharedExitBottomY,
  assetFolder,
  campaignId,
}) {
  const audioItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const count = audioItems.length;
  const selectedFileIndex = activeGraphicIndex && typeof activeGraphicIndex === 'object'
    ? activeGraphicIndex.fileIndex
    : activeGraphicIndex;

  const parentHalfW = parentStepHalfW ?? (parentStep ? halfW(parentStep) : CIRCLE_R);
  const hasParent = parentStepX !== null && parentStepX !== undefined;
  const hasNext = nextStep && nextStepX !== null && nextStepX !== undefined && nextStepY !== null && nextStepY !== undefined;
  const parentIsButton = parentStep?.nodeConfig?.nodeStyle === 'addButton' || parentStep?.nodeConfig?.nodeStyle === 'infoButton';

  const isGridMode = count > 1;
  const gridCols = count > 1 ? 3 : 1;
  const gridRows = Math.max(isGridMode ? Math.ceil(count / gridCols) : count, 1);
  const totalSlots = isGridMode ? gridRows * gridCols : gridRows;
  const gridTotalW = isGridMode ? gridCols * AUDIO_CARD_W + (gridCols - 1) * AUDIO_CARD_GAP : AUDIO_CARD_W;
  const totalH = gridRows * (AUDIO_CARD_H + AUDIO_CARD_GAP) - AUDIO_CARD_GAP;
  const TOP_PAD = 20;
  const cardLeftX = stepX - AUDIO_CARD_W / 2;
  const stackTopY = stepCY - totalH / 2 + TOP_PAD / 2;
  const wrapperPadX = 14;
  const wrapperPadY = 12;
  const wrapperTopY = stackTopY - wrapperPadY;
  const wrapperH = totalH + wrapperPadY * 2;
  const wrapperX = cardLeftX - wrapperPadX;
  const wrapperW = gridTotalW + wrapperPadX * 2;

  const sourceX = hasParent ? parentStepX + parentHalfW : cardLeftX - BRANCH_STEM;
  const sourceY = parentStepY ?? stepCY;
  const addX = wrapperX - PLUS_R - 8;
  const infoX = addX - (PLUS_R * 2) - 8;
  const firstCardY = stackTopY + AUDIO_CARD_H / 2;
  const lastCardY = stackTopY + (gridRows - 1) * (AUDIO_CARD_H + AUDIO_CARD_GAP) + AUDIO_CARD_H / 2;
  const entryAnchorX = wrapperX;
  const entryAnchorY = parentIsButton ? sourceY : stepCY;
  const controlsY = parentIsButton ? sourceY : entryAnchorY;
  const entrySpineBottomY = sharedEntryBottomY ?? Math.max(lastCardY, stepCY);
  const hasSharedBranchStem = sharedBranchX !== null && sharedBranchX !== undefined;
  const branchStemX = hasSharedBranchStem
    ? sharedBranchX
    : Math.min(infoX - PLUS_R - 14, sourceX + 140);

  // Exit connectors should anchor to the outer wrapper edge so expanded grids
  // connect from the container, not from an individual card column.
  const rightEdgeX = wrapperX + wrapperW;
  const exitSpineX = hideExitTrunk ? rightEdgeX : rightEdgeX + 34;
  const nextAnchorX = hasNext ? nextStepX - (nextStepHalfW || 0) : null;
  const branchColor = audioItems.length > 0 ? C.purple : C.orange;
  const exitSpineBottomY = sharedExitBottomY ?? (hideExitTrunk ? lastCardY : Math.max(lastCardY, nextStepY ?? lastCardY));

  const hasHeader = !!step.stepTitle;
  const effectiveWrapperTopY = hasHeader ? wrapperTopY - WRAPPER_HEADER_H : wrapperTopY;
  const effectiveWrapperH = hasHeader ? wrapperH + WRAPPER_HEADER_H : wrapperH;
  const interceptAddPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const triggerAddItemClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddItemClick?.();
  };

  return (
    <g className="fc-node fc-node--audio-stack">
      <rect
        x={wrapperX}
        y={effectiveWrapperTopY}
        width={wrapperW}
        height={effectiveWrapperH}
        rx="14"
        fill="rgba(145,70,255,0.04)"
        stroke={branchColor}
        strokeWidth="1"
        strokeOpacity="0.32"
        style={{ pointerEvents: 'none' }}
      />
      {hasHeader && (
        <>
          <clipPath id={`ash-hdr-${step.stepId}`}>
            <rect x={wrapperX} y={effectiveWrapperTopY} width={wrapperW} height={WRAPPER_HEADER_H} />
          </clipPath>
          <rect
            x={wrapperX}
            y={effectiveWrapperTopY}
            width={wrapperW}
            height={WRAPPER_HEADER_H + 14}
            rx="14" ry="14"
            clipPath={`url(#ash-hdr-${step.stepId})`}
            fill="rgba(145,70,255,0.20)"
            style={{ pointerEvents: 'none' }}
          />
          <text
            x={wrapperX + 14}
            y={effectiveWrapperTopY + WRAPPER_HEADER_H / 2 + 5}
            fontSize="16"
            fill={branchColor}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >♪</text>
          <text
            x={wrapperX + 36}
            y={effectiveWrapperTopY + WRAPPER_HEADER_H / 2 + 5}
            fontSize="11"
            fontWeight="700"
            fill="var(--contrast-grey-1)"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >{step.stepTitle}</text>
        </>
      )}
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          {!hideSourceTrunk && !parentIsButton && (
            <>
              {/* Use the same branch-stem pattern as standard nodes: leave the parent,
                  move to a shared stem, then enter the audio control cluster. */}
              {!hasSharedBranchStem && branchStemX > sourceX + 1 && (
                <line x1={sourceX} y1={sourceY} x2={branchStemX} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              )}
              {Math.abs(sourceY - entryAnchorY) > 2 && (
                <line x1={branchStemX} y1={sourceY} x2={branchStemX} y2={entryAnchorY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              )}
              {branchStemX < infoX - PLUS_R && (
                <line x1={branchStemX} y1={entryAnchorY} x2={infoX - PLUS_R} y2={entryAnchorY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              )}
              <line x1={infoX - PLUS_R} y1={controlsY} x2={entryAnchorX} y2={controlsY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              <line x1={infoX + PLUS_R} y1={controlsY} x2={addX - PLUS_R} y2={controlsY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              <line x1={addX + PLUS_R} y1={controlsY} x2={entryAnchorX} y2={controlsY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
            </>
          )}
          {!hideSourceTrunk && parentIsButton && (
            <>
              <line x1={sourceX} y1={sourceY} x2={entryAnchorX} y2={sourceY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              <circle cx={entryAnchorX} cy={sourceY} r="3" fill={branchColor} fillOpacity="0.9" />
              {Math.abs(sourceY - entryAnchorY) > 2 && (
                <line x1={entryAnchorX} y1={sourceY} x2={entryAnchorX} y2={entryAnchorY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
              )}
            </>
          )}
          {(gridRows > 1 || Math.abs(entryAnchorY - firstCardY) > 2 || Math.abs(entryAnchorY - entrySpineBottomY) > 2) && (
            <line x1={entryAnchorX} y1={Math.min(firstCardY, entryAnchorY)} x2={entryAnchorX} y2={entrySpineBottomY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {!isGridMode && Array.from({ length: gridRows }, (_, rowIdx) => {
            const cy = stackTopY + rowIdx * (AUDIO_CARD_H + AUDIO_CARD_GAP) + AUDIO_CARD_H / 2;
            return (
              <React.Fragment key={`audio-branch-${rowIdx}`}>
                <line x1={entryAnchorX} y1={cy} x2={cardLeftX} y2={cy} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
                <circle cx={cardLeftX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          {!hideSourceTrunk && !parentIsButton && (
            <text x={addX} y={controlsY - PLUS_R - 8} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={C.greenText} style={{ userSelect: 'none' }}>
              Add Audio
            </text>
          )}
        </g>
      )}

      {hasParent && !hideSourceTrunk && !parentIsButton && (
        <g onClick={(e) => { e.stopPropagation(); onInfoClick?.(); }} style={{ cursor: 'pointer' }}>
          <circle cx={infoX} cy={controlsY} r={PLUS_R} fill={C.neonBlue} />
          <text x={infoX} y={controlsY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            i
          </text>
        </g>
      )}

      {hasParent && !hideSourceTrunk && !parentIsButton && (
        <g
          onMouseDown={interceptAddPointerDown}
          onPointerDown={interceptAddPointerDown}
          onClick={triggerAddItemClick}
          style={{ cursor: 'pointer' }}
        >
          <circle cx={addX} cy={controlsY} r={PLUS_R + 7} fill="transparent" />
          <circle cx={addX} cy={controlsY} r={PLUS_R} fill={C.greenText} />
          <text x={addX} y={controlsY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="300" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>
            +
          </text>
        </g>
      )}

      {Array.from({ length: totalSlots }, (_, slotIdx) => {
        const gridRow = isGridMode ? Math.floor(slotIdx / gridCols) : slotIdx;
        const gridCol = isGridMode ? slotIdx % gridCols : 0;
        const cardX = cardLeftX + gridCol * (AUDIO_CARD_W + AUDIO_CARD_GAP);
        const cy = stackTopY + gridRow * (AUDIO_CARD_H + AUDIO_CARD_GAP) + AUDIO_CARD_H / 2;
        const top = cy - AUDIO_CARD_H / 2;
        const isGhost = slotIdx >= count;
        const item = isGhost ? null : audioItems[slotIdx];
        const isSelected = !isGhost && activeStepId === step.stepId && selectedFileIndex === slotIdx;
        const audioFieldInput = item?.audioFilename ?? (item?.srcUrl ? item : item?.filename);
        const audioField = normalizeAudioField(audioFieldInput);
        const audioSrcUrl = audioField.srcUrl
          ? audioField.srcUrl
          : audioField.name ? audioUrl(audioField.name, audioField.isDefault, assetFolder, campaignId) : null;
        const filename = audioField.name || 'No audio selected';
        const title = item?.name?.trim?.() || `Audio ${slotIdx + 1}`;
        const volume = item?.volume !== undefined ? item.volume : 100;
        const isLooping = item?.loop === true;
        const isDisabled = item?.playSFX === false;
        const loopIconSize = 18;

        if (isGhost && count > 0) {
          return (
            <g key={`${step.stepId}-ghost-${slotIdx}`} style={{ pointerEvents: 'none' }}>
              <rect
                x={cardX}
                y={top}
                width={AUDIO_CARD_W}
                height={AUDIO_CARD_H}
                rx="8"
                fill="rgba(255,255,255,0.025)"
                stroke={branchColor}
                strokeWidth="1"
                strokeOpacity="0.2"
                strokeDasharray="6 4"
              />
              <text
                x={cardX + AUDIO_CARD_W / 2}
                y={cy}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="28"
                fontWeight="200"
                fill={branchColor}
                fillOpacity="0.12"
                style={{ userSelect: 'none' }}
              >
                +
              </text>
            </g>
          );
        }

        const isSingleDisabled = count === 1 && isDisabled;

        // Single disabled: render a ghost-style clickable card to re-enable
        if (isSingleDisabled) {
          return (
            <g
              key={`${step.stepId}-${slotIdx}`}
              onMouseDown={interceptAddPointerDown}
              onPointerDown={interceptAddPointerDown}
              onClick={triggerAddItemClick}
              style={{ cursor: 'pointer' }}
            >
              <rect x={cardX - 4} y={top - 4} width={AUDIO_CARD_W + 8} height={AUDIO_CARD_H + 8} rx="10" fill="transparent" />
              <rect x={cardX + 2} y={top + 4} width={AUDIO_CARD_W} height={AUDIO_CARD_H} rx="8" fill="#000" fillOpacity="0.2" style={{ pointerEvents: 'none' }} />
              <rect
                x={cardX}
                y={top}
                width={AUDIO_CARD_W}
                height={AUDIO_CARD_H}
                rx="8"
                fill="rgba(255,255,255,0.025)"
                stroke={branchColor}
                strokeWidth="1"
                strokeOpacity="0.4"
                strokeDasharray="6 4"
              />
              <text x={cardX + AUDIO_CARD_W / 2} y={cy - 10} textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="200" fill={branchColor} fillOpacity="0.5" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                +
              </text>
              <text x={cardX + AUDIO_CARD_W / 2} y={cy + 14} textAnchor="middle" fontSize="9" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                Click to add audio
              </text>
            </g>
          );
        }

        return (
          <g key={`${step.stepId}-${slotIdx}`} style={isDisabled && count > 1 ? { opacity: 0.5 } : undefined}>
            <rect x={cardX + 2} y={top + 4} width={AUDIO_CARD_W} height={AUDIO_CARD_H} rx="8" fill="#000" fillOpacity="0.3" style={{ pointerEvents: 'none' }} />
            <rect
              x={cardX}
              y={top}
              width={AUDIO_CARD_W}
              height={AUDIO_CARD_H}
              rx="8"
              fill={isSelected ? C.cardSel : C.card}
              stroke={isSelected ? C.sel : branchColor}
              strokeWidth={isSelected ? 2 : 1.5}
              style={{ cursor: item ? 'pointer' : 'default' }}
              onClick={item ? (e) => { e.stopPropagation(); onAudioClick?.(slotIdx); } : undefined}
            />

            <line
              x1={cardX + 1}
              y1={top + HEADER_H}
              x2={cardX + AUDIO_CARD_W - 1}
              y2={top + HEADER_H}
              stroke={C.border}
              strokeWidth="1"
              style={{ pointerEvents: 'none' }}
            />

            <text x={cardX + PAD} y={top + 19} fontSize="11" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {title}
            </text>

            {item ? (
              <>
                <text x={cardX + PAD} y={top + 46} fontSize="8.5" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  File
                </text>
                <text x={cardX + PAD} y={top + 60} fontSize="9.5" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  {filename.length > 28 ? `${filename.slice(0, 28)}...` : filename}
                </text>

                    <foreignObject
                      x={cardX + PAD}
                      y={top + 70}
                      width={AUDIO_CARD_W - PAD * 2}
                      height={WAVE_H}
                      onClick={(e) => { e.stopPropagation(); }}
                    >
                      <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%' }}>
                        <AudioWaveformCell
                          srcUrl={audioSrcUrl}
                          height={WAVE_H}
                          onSelect={() => onAudioClick?.(slotIdx)}
                        />
                      </div>
                    </foreignObject>

                    {isLooping && (
                      <>
                        <image
                          href={audioLoopIcon}
                          x={cardX + PAD}
                          y={top + AUDIO_CARD_H - 32}
                          width={loopIconSize}
                          height={loopIconSize}
                          style={{ filter: 'brightness(0) saturate(100%) invert(71%) sepia(39%) saturate(642%) hue-rotate(90deg) brightness(94%) contrast(89%)' }}
                        />
                        <text x={cardX + PAD + loopIconSize + 4} y={top + AUDIO_CARD_H - 18} fontSize="9" fill={C.greenText} style={{ pointerEvents: 'none', userSelect: 'none', fontWeight: 600 }}>
                          Looped
                        </text>
                      </>
                    )}

                    <rect x={cardX + AUDIO_CARD_W - PAD - 54} y={top + AUDIO_CARD_H - 30} width={54} height={18} rx={5} ry={5} fill="rgba(255,255,255,0.07)" style={{ pointerEvents: 'none' }} />
                    <text x={cardX + AUDIO_CARD_W - PAD - 27} y={top + AUDIO_CARD_H - 17} fontSize="9" fill={C.textMuted} textAnchor="middle" style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      VOL {volume}%
                    </text>

                {onRemoveAudio && !isDisabled && (
                  <g
                    onMouseDown={(e) => {
                      console.log('[AudioStackNode] delete mouse down', { stepId: step?.stepId, slotIdx, title, filename });
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onPointerDown={(e) => {
                      console.log('[AudioStackNode] delete pointer down', { stepId: step?.stepId, slotIdx, title, filename });
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      console.log('[AudioStackNode] delete click', { stepId: step?.stepId, slotIdx, title, filename, hasHandler: !!onRemoveAudio });
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveAudio(slotIdx);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={cardX + AUDIO_CARD_W - PAD - 15}
                      y={top + HEADER_H / 2 - 15}
                      width={30}
                      height={30}
                      rx="15"
                      fill="transparent"
                    />
                    <circle cx={cardX + AUDIO_CARD_W - PAD - 2} cy={top + HEADER_H / 2} r={9} fill="#dc2626" />
                    <g transform={`translate(${cardX + AUDIO_CARD_W - PAD - 2}, ${top + HEADER_H / 2})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      <rect x="-2" y="-5" width="4" height="2" rx="1" fill="#fee2e2" />
                      <rect x="-4.5" y="-3" width="9" height="1.5" rx="0.75" fill="#fee2e2" />
                      <rect x="-3.5" y="-1" width="7" height="5.5" rx="0.8" fill="#fee2e2" />
                      <rect x="-1.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                      <rect x="0.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                    </g>
                  </g>
                )}
              </>
            ) : (
              <>
                <text x={cardX + AUDIO_CARD_W / 2} y={top + 58} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  No audio selected
                </text>
                <text x={cardX + AUDIO_CARD_W / 2} y={top + AUDIO_CARD_H - 16} textAnchor="middle" fontSize="8.5" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  Click the add button to create your first audio node
                </text>
              </>
            )}
          </g>
        );
      })}

      {hasNext && nextAnchorX !== null && (
        <g style={{ pointerEvents: 'none' }}>
          {isGridMode && rightEdgeX < nextAnchorX && (
            <>
              <line
                x1={rightEdgeX}
                y1={nextStepY}
                x2={nextAnchorX}
                y2={nextStepY}
                stroke={branchColor}
                strokeWidth="1.5"
                strokeOpacity="0.78"
                strokeLinecap="square"
              />
              <circle cx={rightEdgeX} cy={nextStepY} r="3" fill={branchColor} fillOpacity="0.9" />
            </>
          )}
          {!isGridMode && Array.from({ length: gridRows }, (_, rowIdx) => {
            const cy = stackTopY + rowIdx * (AUDIO_CARD_H + AUDIO_CARD_GAP) + AUDIO_CARD_H / 2;
            return (
              <React.Fragment key={`audio-exit-${rowIdx}`}>
                <line x1={rightEdgeX} y1={cy} x2={exitSpineX} y2={cy} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
                <circle cx={rightEdgeX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          {!isGridMode && (gridRows > 1 || Math.abs(stepCY - nextStepY) > 2) && (
            <line x1={exitSpineX} y1={Math.min(firstCardY, nextStepY)} x2={exitSpineX} y2={exitSpineBottomY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {!isGridMode && !hideExitTrunk && exitSpineX < nextAnchorX && (
            <line x1={exitSpineX} y1={nextStepY} x2={nextAnchorX} y2={nextStepY} stroke={branchColor} strokeWidth="1.5" strokeOpacity="0.78" strokeLinecap="square" />
          )}
        </g>
      )}
    </g>
  );
}